import React from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography
} from '@mui/material';
import { useAutoTranslation } from 'src/hooks/useTranslate';
import { english } from 'src/i18n/language';

/**
 * The "Discard unsaved changes?" confirmation, shared by the sidebar's save
 * footer and the workspace tab strip's close guard (TGCLOUD-1839), so the two
 * prompts about the same unsaved edits cannot drift apart.
 *
 * House style: the painted DialogTitle with `pb: 1`, the `pt: 16px !important`
 * that stops MUI collapsing the body into the title, the SAFE action on the
 * left with the focus, and the destructive one on the right as contained
 * error.
 *
 * `dense` is for a prompt read inside a panel (body2 text and small buttons,
 * which is what the save footer has always used). A prompt raised from the
 * page chrome, like closing a tab, is read next to the canvas and takes the
 * default sizes.
 *
 * Props:
 *   open          whether the dialog is shown
 *   onCancel      keep the changes (the default action; Escape and a backdrop
 *                 click do this too)
 *   onConfirm     discard them
 *   title, cancelLabel, confirmLabel, ariaLabel
 *                 already translated by the caller; each one left out takes
 *                 this dialog's own wording ("Discard unsaved changes?",
 *                 "Cancel", "Discard", "Confirm discard"), translated here
 *   dense         body2 + small buttons
 *   children      the sentence saying what will be lost
 */
export default function DiscardChangesDialog({
  open,
  onCancel,
  onConfirm,
  title,
  cancelLabel,
  confirmLabel,
  ariaLabel,
  dense = false,
  children
}) {
  const t = useAutoTranslation();
  const buttonSize = dense ? 'small' : undefined;
  return (
    <Dialog
      open={open}
      onClose={onCancel}
      maxWidth="xs"
      fullWidth
      role="alertdialog"
      aria-label={ariaLabel ?? t('Confirm discard')}
    >
      <DialogTitle sx={{ pb: 1 }}>
        {title ?? t('Discard unsaved changes?')}
      </DialogTitle>
      <DialogContent sx={{ pt: '16px !important' }}>
        {/* A paragraph, so `children` must be phrasing content. */}
        <Typography
          variant={dense ? 'body2' : 'body1'}
          sx={{
            color: 'text.primary'
          }}
        >
          {children}
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 2, pb: 2 }}>
        <Button
          variant="outlined"
          size={buttonSize}
          autoFocus
          onClick={onCancel}
        >
          {cancelLabel ?? t('Cancel')}
        </Button>
        <Button
          variant="contained"
          size={buttonSize}
          color="error"
          onClick={onConfirm}
        >
          {confirmLabel ?? t('Discard')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * "Person and Diseases" — how the prompts join dirty section labels. The
 * labels are useDirtyState's English section names, translated here for
 * display; pass the component's `t`.
 */
export const joinSectionLabels = (labels = [], t = english) =>
  labels
    .map((label) => t(label))
    .reduce(
      (joined, label) =>
        joined
          ? t('{{first}} and {{second}}', { first: joined, second: label })
          : label,
      ''
    );

/**
 * What a prompt says about unsaved edits spread over open TABS: one tab is
 * named with its sections, several are listed. Shared by the tab strip's close
 * guard and the app bar's Log Out / user-group switcher, so a clinician is told
 * the same thing whichever of them is about to discard the work. Phrasing
 * content only, because DiscardChangesDialog puts it in a paragraph.
 *
 * `sessions` are workspace sessions (or the same shape): { sessionId, title,
 * unsavedSections }.
 */
export function UnsavedTabsSummary({ sessions = [] }) {
  const t = useAutoTranslation();

  if (sessions.length === 1) {
    return (
      <>
        {t('Unsaved changes to')}{' '}
        <strong>{joinSectionLabels(sessions[0].unsavedSections, t)}</strong>{' '}
        {t('in')} <strong>{sessions[0].title}</strong> {t('will be lost.')}
      </>
    );
  }
  if (sessions.length > 1) {
    return (
      <>
        {t('Unsaved changes in {{count}} tabs will be lost:', {
          count: sessions.length
        })}{' '}
        {sessions.map((s, i) => (
          <React.Fragment key={s.sessionId}>
            {i > 0 ? ', ' : ''}
            <strong>{s.title}</strong> (
            {joinSectionLabels(s.unsavedSections, t)})
          </React.Fragment>
        ))}
        .
      </>
    );
  }
  return t('All unsaved changes will be lost.');
}
